import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from 'utils/supabase';
import { Button, CircularProgress } from '@mui/material';
// @ts-ignore
import logo from 'assets/prb-gold.png';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type Decision = 'Tak' | 'Nie';

/** Must match `StdStatus` / admin `STD_STATUS_OPTIONS` in Invitations.tsx */
const STD_CONFIRM = 'STD potwierdzenie udziału' as const;
const STD_DECLINE = 'STD odmowa udziału' as const;

interface InvitationType {
  id: string;
  firstNameA: string;
  lastNameA: string;
  firstNameB: string | null;
  lastNameB: string | null;
  stdStatus: string | null;
  paperInvitation: boolean | null;
}

const formatAddressNames = (invitation: InvitationType): string => {
  const firstNameA = invitation.firstNameA?.trim() || '';
  const lastNameA = invitation.lastNameA?.trim() || '';
  const firstNameB = invitation.firstNameB?.trim() || '';
  const lastNameB = invitation.lastNameB?.trim() || '';
  
  const fullNameA = `${firstNameA} ${lastNameA}`.trim();
  const fullNameB = `${firstNameB} ${lastNameB}`.trim();
  
  if (!fullNameA) return '';
  // Only include 'oraz' and second name if both firstName AND lastName of person B exist
  if (!firstNameB || !lastNameB) return fullNameA;
  
  return `${fullNameA} oraz ${fullNameB}`;
};

const InvitationResponse = () => {
  const { link } = useParams();
  const [invitation, setInvitation] = useState<InvitationType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaperChoice, setShowPaperChoice] = useState(false);

  useEffect(() => {
    document.title = 'Nagroda PRB: Save the Date';
    return () => {
      document.title = 'Formularz Nominacji do nagrody PRB';
    };
  }, []);

  useEffect(() => {
    fetchInvitation();
  }, [link]);

  /** Resume paper vs electronic choice if attendance was saved but form not completed */
  useEffect(() => {
    if (!invitation) return;
    if (invitation.stdStatus === STD_CONFIRM && invitation.paperInvitation == null) {
      setShowPaperChoice(true);
    }
  }, [invitation]);

  const fetchInvitation = async () => {
    try {
      if (!link) throw new Error('Missing link param')
      const token = link.trim()
      const currentPathUrl = `${window.location.origin}/zaproszenie/${token}`
      const currentHrefNoQuery = window.location.href.split('?')[0].split('#')[0]
      const envBase = (process.env.REACT_APP_URL || '').replace(/\/$/, '')
      const candidateUrls = Array.from(new Set([
        currentPathUrl,
        `${currentPathUrl}/`,
        currentHrefNoQuery,
        envBase ? `${envBase}/zaproszenie/${token}` : '',
        envBase ? `${envBase}/zaproszenie/${token}/` : '',
        `https://nagroda.prb.pl/zaproszenie/${token}`,
        `https://nagroda.prb.pl/zaproszenie/${token}/`,
        `https://prbnagroda.pl/zaproszenie/${token}`,
        `https://prbnagroda.pl/zaproszenie/${token}/`
      ].filter(Boolean)))

      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .in('stdLink', candidateUrls)
        .limit(1)

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Nie znaleziono zaproszenia');

      setInvitation(data[0]);
    } catch (err) {
      console.error("Invitation fetch error:", err);
      setError('Nie znaleziono zaproszenia lub link jest nieprawidłowy');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (decision: Decision) => {
    if (!invitation) return;

    try {
      const stdStatus = decision === 'Tak' ? STD_CONFIRM : STD_DECLINE;

      const { error } = await supabase
        .from('invitations')
        .update({ stdStatus })
        .eq('id', invitation.id);

      if (error) throw error;

      if (decision === 'Tak') {
        setShowPaperChoice(true);
        setInvitation(prev => (prev ? { ...prev, stdStatus } : null));
      } else {
        // Show toast for 'Nie' response
        toast.success('Dziękujemy za odpowiedź!');
        await fetchInvitation();
      }
    } catch (err) {
      console.error('Error updating decision:', err);
      setError('Wystąpił błąd podczas zapisywania decyzji');
    }
  };

  const handlePaperChoice = async (wantsPaper: boolean) => {
    if (!invitation) return;

    try {
      const { error } = await supabase
        .from('invitations')
        .update({
          paperInvitation: wantsPaper
        })
        .eq('id', invitation.id);

      if (error) throw error;
      
      // Show toast after paper choice
      toast.success('Dziękujemy za odpowiedź!');
      // Hide the paper choice view
      setShowPaperChoice(false);
      // Fetch updated invitation data
      await fetchInvitation();
    } catch (err) {
      console.error('Error updating paper choice:', err);
      setError('Wystąpił błąd podczas zapisywania wyboru');
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <ErrorMessage>{error}</ErrorMessage>
      </Wrapper>
    );
  }

  if (!invitation) {
    return <Navigate to="/" />;
  }

  return (
    <Wrapper>
      <ContentCard>
        <LogoWrapper>
          <Logo src={logo} alt="PRB Logo" />
        </LogoWrapper>
        <h1>Save the Date</h1>
        <h2>Sz.P. {formatAddressNames(invitation)}</h2>
        <h2><b>Czy będziesz uczestniczyć w Gali Nagrody Polskiej Rady Biznesu?</b></h2>
        <p>Wydarzenie odbędzie się w poniedziałek 25 maja 2026 roku o godz. 19:30 w Ufficio Primo w Warszawie.</p>
        <p>Potwierdzam udział w Gali:</p>
        {!showPaperChoice ? (
          <>
            <ButtonsContainer>
              <StyledButton
                variant="contained"
                selected={invitation.stdStatus === STD_CONFIRM}
                onClick={() => handleDecision('Tak')}
                disabled={invitation.stdStatus === STD_CONFIRM}
              >
                Tak
              </StyledButton>
              <StyledButton
                variant="contained"
                selected={invitation.stdStatus === STD_DECLINE}
                onClick={() => handleDecision('Nie')}
                disabled={invitation.stdStatus === STD_DECLINE}
              >
                Nie
              </StyledButton>
            </ButtonsContainer>
            {invitation.stdStatus === STD_CONFIRM && (
              <ResponseMessage>
                Dziękujemy za odpowiedź: <strong>Tak</strong>
                {invitation.paperInvitation !== null && (
                  <div>
                    Wybrana forma zaproszenia:{' '}
                    <strong>
                      {invitation.paperInvitation ? 'Papierowa' : 'Elektroniczna'}
                    </strong>
                  </div>
                )}
              </ResponseMessage>
            )}
            {invitation.stdStatus === STD_DECLINE && (
              <ResponseMessage>
                Dziękujemy za odpowiedź: <strong>Nie</strong>
              </ResponseMessage>
            )}
          </>
        ) : (
          <>
            <h2>Wybieram zaproszenie:</h2>
            <ButtonsContainer>
              <StyledButton
                variant="contained"
                onClick={() => handlePaperChoice(true)}
              >
                papierowe
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={() => handlePaperChoice(false)}
              >
                elektroniczne
              </StyledButton>
            </ButtonsContainer>
          </>
        )}
      </ContentCard>
      <ToastContainer position="bottom-center" autoClose={5000} />
    </Wrapper>
  );
};

export default InvitationResponse;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #f5f5f5;
`;

const ContentCard = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  text-align: center;

  h1 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 24px;
  }

  h2 {
    margin: 24px 0;
  }

  p {
    font-size: 18px;
    margin-bottom: 24px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin: 24px 0;
`;

const StyledButton = styled(Button)<{ selected?: boolean }>`
  && {
    width: 250px;
    padding: 16px 24px;
    margin: 8px;
    border: 2px solid #C5A572;
    background-color: ${({ selected }) => selected ? '#C5A572' : 'white'};
    color: ${({ selected }) => selected ? 'white' : '#C5A572'};
    
    &:hover {
      background-color: ${({ selected }) => selected ? '#C5A572' : 'rgba(197, 165, 114, 0.1)'};
      border-color: #C5A572;
    }

    &:disabled {
      background-color: ${({ selected }) => selected ? '#C5A572' : 'white'};
      border-color: #C5A572;
      color: ${({ selected }) => selected ? 'white' : '#C5A572'};
      opacity: 0.7;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  font-size: 18px;
  padding: 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ResponseMessage = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: #f8f8f8;
  border-radius: 4px;
  line-height: 1.6;
`;

const LogoWrapper = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.img`
  width: 150px;
  height: auto;
`; 